/**
 * useVehicleFipeEnrichment - Enriquecimento FIPE de veículos importados
 *
 * Resolve marca/modelo/ano OFICIAIS da base global FIPE (IDs relacionais) a
 * partir dos dados brutos devolvidos por um provider externo (ERP), para que
 * o veículo importado fique com os mesmos vínculos do cadastro manual.
 *
 * Cascata de resolução (reaproveita 100% a estrutura já existente):
 *   1. Tem marca + modelo em texto? -> ação `resolve` do `fipe-proxy`
 *      (useFipeResolve), usando o código FIPE para confirmar a correspondência.
 *   2. Não resolveu e tem placa válida? -> `plate-proxy` (usePlateLookup),
 *      aproveitando o `fipe_match` já calculado pela Edge Function.
 *   3. Nada resolveu -> mantém apenas os textos do provider.
 *
 * Falhas são representadas como `unmatched`; o consumidor decide se pode gravar.
 *
 * @version 1.2.0
 * RELEASE: 2026-08-23 v1.2.0 - Resolução MANUAL (status `manual`): quando o provider não
 *          devolve dados compatíveis com a FIPE, o usuário escolhe marca/modelo/ano (ou
 *          categoria própria do tenant) e o resultado entra no mesmo contrato de enriquecimento.
 * RELEASE: 2026-08-23 v1.1.0 - enrichAll passa a devolver um mapa imutável dos
 *          resultados, eliminando a corrida entre atualização de estado e persistência.
 * RELEASE: 2026-08-23 v1.0.0 - Criação inicial (importação automática SGCNET)
 */

import { useCallback, useState } from 'react';

import { createLogger } from '@/lib/logger';
import { useFipeResolve, type FipeVehicleType } from '@/hooks/useFipeLookup';
import { usePlateLookup, isValidPlate, normalizePlateInput } from '@/hooks/usePlateLookup';
import type { VehicleType } from '@/hooks/useCustomerVehicles';

const logger = createLogger('useVehicleFipeEnrichment');

/** Situação do enriquecimento de um veículo */
export type EnrichmentStatus =
  | 'idle'
  | 'loading'
  | 'confirmed'
  | 'suggested'
  | 'unmatched'
  /** Marca/modelo/ano (ou categoria) definidos manualmente pelo usuário */
  | 'manual';

/** Resultado do enriquecimento de um veículo */
export interface VehicleEnrichment {
  status: EnrichmentStatus;
  /** Origem da correspondência (para exibição/diagnóstico) */
  source?: 'fipe' | 'plate' | 'manual';
  /** v1.2.0: tipo escolhido manualmente (permite mover o veículo para "Outros") */
  vehicle_type?: VehicleType | null;
  /** v1.2.0: categoria do tenant, usada quando o tipo é OTHER (fora da FIPE) */
  category_id?: string | null;
  brand_id?: string | null;
  brand_name?: string | null;
  model_id?: string | null;
  model_name?: string | null;
  year_code?: string | null;
  year?: number | null;
  fuel?: string | null;
  fipe_code?: string | null;
  fipe_value?: number | null;
}

/** Entrada mínima esperada de um veículo bruto do provider */
export interface EnrichableVehicle {
  external_key: string;
  vehicle_type: VehicleType;
  brand_name: string | null;
  model_name: string | null;
  year: number | null;
  fuel: string | null;
  plate: string | null;
  fipe_code: string | null;
  fipe_value: number | null;
}

/** Extrai o ano numérico de um rótulo FIPE ("2003 Gasolina") */
function parseYearFromLabel(label?: string | null): number | null {
  const match = String(label || '').match(/(19|20)\d{2}/);
  return match ? Number(match[0]) : null;
}

export function useVehicleFipeEnrichment() {
  const fipeResolve = useFipeResolve();
  const plateLookup = usePlateLookup();

  /** Mapa external_key -> resultado do enriquecimento */
  const [enrichment, setEnrichment] = useState<Record<string, VehicleEnrichment>>({});

  const setStatus = useCallback((key: string, value: VehicleEnrichment) => {
    setEnrichment((prev) => ({ ...prev, [key]: value }));
  }, []);

  /**
   * v1.2.0: grava uma resolução MANUAL para o veículo (marca/modelo/ano FIPE
   * escolhidos pelo usuário, ou categoria própria quando o tipo é OTHER).
   */
  const setManualMatch = useCallback(
    (externalKey: string, match: Omit<VehicleEnrichment, 'status' | 'source'>) => {
      setStatus(externalKey, { ...match, status: 'manual', source: 'manual' });
    },
    [setStatus],
  );

  /** Limpa o estado (ao fechar/reabrir o wizard) */
  const reset = useCallback(() => setEnrichment({}), []);

  /**
   * Enriquece um veículo. `forcePlate` força a consulta por placa mesmo quando
   * a resolução por texto já funcionou (ação manual "Resolver FIPE").
   */
  const enrichVehicle = useCallback(
    async (vehicle: EnrichableVehicle, forcePlate = false): Promise<VehicleEnrichment> => {
      // Tipo OTHER é categoria do tenant — fora da tabela FIPE
      if (vehicle.vehicle_type === 'OTHER') {
        const result: VehicleEnrichment = { status: 'unmatched' };
        setStatus(vehicle.external_key, result);
        return result;
      }

      const fipeType = vehicle.vehicle_type as FipeVehicleType;
      setStatus(vehicle.external_key, { status: 'loading' });

      // ---------------------------------------------------------------------
      // 1) Resolução por texto (marca + modelo) na base global FIPE
      // ---------------------------------------------------------------------
      if (!forcePlate && (vehicle.brand_name || vehicle.model_name)) {
        try {
          const resolved = await fipeResolve.mutateAsync({
            vehicle_type: fipeType,
            brand_text: vehicle.brand_name,
            model_text: vehicle.model_name,
            year: vehicle.year,
            fuel: vehicle.fuel,
            fipe_code: vehicle.fipe_code,
          });

          if (resolved.matched) {
            const result: VehicleEnrichment = {
              status: resolved.confidence === 'confirmed' ? 'confirmed' : 'suggested',
              source: 'fipe',
              brand_id: resolved.brand_id ?? null,
              brand_name: resolved.brand_name ?? vehicle.brand_name,
              model_id: resolved.model_id ?? null,
              model_name: resolved.model_name ?? vehicle.model_name,
              year_code: resolved.year_code ?? null,
              year: parseYearFromLabel(resolved.year_label) ?? vehicle.year,
              fuel: vehicle.fuel,
              // Só sobrescreve o que o provider não enviou
              fipe_code: vehicle.fipe_code || resolved.fipe_code || null,
              fipe_value: vehicle.fipe_value ?? resolved.valor ?? null,
            };
            setStatus(vehicle.external_key, result);
            return result;
          }
        } catch (error) {
          logger.warn('Falha ao resolver FIPE por texto', {
            plate: vehicle.plate,
            message: error instanceof Error ? error.message : String(error),
          });
        }
      }

      // ---------------------------------------------------------------------
      // 2) Fallback: consulta por placa (mesma estrutura do modal de veículo)
      // ---------------------------------------------------------------------
      if (vehicle.plate && isValidPlate(vehicle.plate)) {
        try {
          const data = await plateLookup.mutateAsync(normalizePlateInput(vehicle.plate));
          const match = data.fipe_match?.matched ? data.fipe_match : null;

          if (match) {
            const result: VehicleEnrichment = {
              status: match.confidence === 'confirmed' ? 'confirmed' : 'suggested',
              source: 'plate',
              brand_id: match.brand_id ?? null,
              brand_name: match.brand_name ?? data.brand ?? vehicle.brand_name,
              model_id: match.model_id ?? null,
              model_name: match.model_name ?? data.model ?? vehicle.model_name,
              year_code: match.year_code ?? null,
              year: parseYearFromLabel(match.year_label) ?? data.year ?? vehicle.year,
              fuel: data.fuel ?? vehicle.fuel,
              fipe_code: vehicle.fipe_code || match.fipe_code || data.fipe_code || null,
              fipe_value: vehicle.fipe_value ?? match.valor ?? data.fipe_value ?? null,
            };
            setStatus(vehicle.external_key, result);
            return result;
          }

          // Sem match FIPE, mas a placa devolveu textos melhores que os do ERP
          const partial: VehicleEnrichment = {
            status: 'unmatched',
            source: 'plate',
            brand_name: vehicle.brand_name || data.brand,
            model_name: vehicle.model_name || data.model,
            year: vehicle.year ?? data.year,
            fuel: vehicle.fuel ?? data.fuel,
            fipe_code: vehicle.fipe_code || data.fipe_code || null,
            fipe_value: vehicle.fipe_value ?? data.fipe_value ?? null,
          };
          setStatus(vehicle.external_key, partial);
          return partial;
        } catch (error) {
          logger.warn('Falha na consulta por placa', {
            plate: vehicle.plate,
            message: error instanceof Error ? error.message : String(error),
          });
        }
      }

      const unmatched: VehicleEnrichment = { status: 'unmatched' };
      setStatus(vehicle.external_key, unmatched);
      return unmatched;
    },
    [fipeResolve, plateLookup, setStatus],
  );

  /** Enriquece uma lista de veículos em sequência (evita estourar o provedor) */
  const enrichAll = useCallback(
    async (vehicles: EnrichableVehicle[]): Promise<Record<string, VehicleEnrichment>> => {
      const results: Record<string, VehicleEnrichment> = {};
      for (const vehicle of vehicles) {
        results[vehicle.external_key] = await enrichVehicle(vehicle);
      }
      return results;
    },
    [enrichVehicle],
  );

  return { enrichment, enrichVehicle, enrichAll, setManualMatch, reset };
}
